import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SolPriceContext = createContext();

export const useSolPrice = () => {
  const context = useContext(SolPriceContext);
  if (!context) {
    throw new Error('useSolPrice must be used within a SolPriceProvider');
  }
  return context;
};

const CACHE_KEY = 'sol_price_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const SolPriceProvider = ({ children }) => {
  const [solPrice, setSolPrice] = useState(() => {
    // Try to load cached price on mount
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { price, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < CACHE_DURATION) {
          console.log('💾 Using cached SOL price:', price);
          return price;
        }
      }
    } catch (e) {
      console.warn('Failed to load cached price:', e);
    }
    return 150; // Default fallback
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Save price to cache
  const cachePrice = useCallback((price) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        price,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Failed to cache price:', e);
    }
  }, []);

  // Fetch real-time SOL price with multiple fallbacks
  const fetchSolPrice = useCallback(async (forceRefresh = false) => {
    // Check cache first unless forced
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { price, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < CACHE_DURATION) {
            console.log('💾 Using cached SOL price (age:', Math.round(age / 1000), 'seconds)');
            setSolPrice(price);
            setLastUpdated(new Date(timestamp));
            setIsLoading(false);
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to check cache:', e);
      }
    }
    try {
      // Try CoinGecko first (most reliable, works globally)
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd',
          {
            headers: {
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(8000) // 8 second timeout
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.solana && data.solana.usd) {
            console.log('✅ SOL price fetched from CoinGecko:', data.solana.usd);
            setSolPrice(data.solana.usd);
            cachePrice(data.solana.usd);
            setLastUpdated(new Date());
            setError(null);
            setIsLoading(false);
            setRetryCount(0);
            return; // Success, exit early
          }
        } else if (response.status === 429) {
          console.warn('⚠️ CoinGecko rate limit hit, trying fallbacks...');
        }
      } catch (cgError) {
        console.warn('⚠️ CoinGecko API failed:', cgError.message);
      }

      // Fallback 1: CryptoCompare (free, no API key needed for basic)
      try {
        const response = await fetch(
          'https://min-api.cryptocompare.com/data/price?fsym=SOL&tsyms=USD',
          {
            headers: {
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(8000)
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.USD) {
            console.log('✅ SOL price fetched from CryptoCompare:', data.USD);
            setSolPrice(data.USD);
            cachePrice(data.USD);
            setLastUpdated(new Date());
            setError(null);
            setIsLoading(false);
            setRetryCount(0);
            return;
          }
        }
      } catch (ccError) {
        console.warn('⚠️ CryptoCompare API failed:', ccError.message);
      }

      // Fallback 2: Coinbase public API (no auth required)
      try {
        const response = await fetch(
          'https://api.coinbase.com/v2/prices/SOL-USD/spot',
          {
            headers: {
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(8000)
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.data && data.data.amount) {
            const price = parseFloat(data.data.amount);
            console.log('✅ SOL price fetched from Coinbase:', price);
            setSolPrice(price);
            cachePrice(price);
            setLastUpdated(new Date());
            setError(null);
            setIsLoading(false);
            setRetryCount(0);
            return;
          }
        }
      } catch (cbError) {
        console.warn('⚠️ Coinbase API failed:', cbError.message);
      }

      // Fallback 3: Kraken public API
      try {
        const response = await fetch(
          'https://api.kraken.com/0/public/Ticker?pair=SOLUSD',
          {
            headers: {
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(8000)
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.result && data.result.SOLUSD && data.result.SOLUSD.c && data.result.SOLUSD.c[0]) {
            const price = parseFloat(data.result.SOLUSD.c[0]);
            console.log('✅ SOL price fetched from Kraken:', price);
            setSolPrice(price);
            cachePrice(price);
            setLastUpdated(new Date());
            setError(null);
            setIsLoading(false);
            setRetryCount(0);
            return;
          }
        }
      } catch (krakenError) {
        console.warn('⚠️ Kraken API failed:', krakenError.message);
      }

      // All APIs failed, use fallback
      throw new Error('All price APIs failed');
      
    } catch (error) {
      console.error('❌ Error fetching SOL price from all sources, using fallback:', error.message);
      setError(error.message);
      setIsLoading(false);
      setRetryCount(prev => prev + 1);
      // Keep the current/fallback price if all APIs fail
      console.log(`💡 Using fallback price: $${solPrice}`);
    }
  }, [solPrice, cachePrice]);

  // Initial fetch on mount
  useEffect(() => {
    fetchSolPrice();
  }, [fetchSolPrice]);

  // Set up automatic price updates every 5 minutes to avoid rate limiting
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSolPrice();
    }, 5 * 60 * 1000); // Update every 5 minutes

    return () => clearInterval(interval);
  }, [fetchSolPrice]);

  // Helper function to calculate USD price from SOL amount
  const calculateUsdPrice = useCallback((solAmount) => {
    if (!solAmount || isNaN(solAmount)) return '0.00';
    return (parseFloat(solAmount) * solPrice).toFixed(2);
  }, [solPrice]);

  // Helper function to format SOL price display
  const formatSolPrice = useCallback((solAmount, decimals = 4) => {
    if (!solAmount || isNaN(solAmount)) return '0';
    return parseFloat(solAmount).toFixed(decimals);
  }, []);

  // Helper function to format USD price display
  const formatUsdPrice = useCallback((solAmount, includeSymbol = true) => {
    const usdAmount = calculateUsdPrice(solAmount);
    return includeSymbol ? `$${usdAmount}` : usdAmount;
  }, [calculateUsdPrice]);

  // Manual refresh function for components that need it
  const refreshPrice = useCallback(() => {
    setIsLoading(true);
    return fetchSolPrice(true); // Force refresh, bypass cache
  }, [fetchSolPrice]);

  const value = {
    solPrice,
    isLoading,
    lastUpdated,
    error,
    calculateUsdPrice,
    formatSolPrice,
    formatUsdPrice,
    refreshPrice
  };

  return (
    <SolPriceContext.Provider value={value}>
      {children}
    </SolPriceContext.Provider>
  );
};

export default SolPriceContext;

